import { APPROVAL_STATES } from './approval.types';
import type { ApprovalDecision, ApprovalSignals } from './approval.types';

const PRODUCTION_HOSTS = new Set([
  'alunoconectado.com.br',
  'www.alunoconectado.com.br',
]);

export function isProductionHost(hostname?: string): boolean {
  return PRODUCTION_HOSTS.has(String(hostname || '').toLowerCase());
}

export function isTestAccessHost(hostname?: string): boolean {
  const host = String(hostname || '').toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === 'test.alunoconectado.com.br' ||
    host.endsWith('.ngrok-free.dev')
  );
}

export function shouldShowTestAccess(hostname?: string): boolean {
  return isTestAccessHost(hostname) && !isProductionHost(hostname);
}

export function hasPwaInstallSignal(input: ApprovalSignals): boolean {
  return Boolean(
    input.isStandalone ||
    input.localInstallSignal ||
    Number(input.relatedAppsCount || 0) > 0
  );
}

export function isApprovalSignalExpired(input: ApprovalSignals, nowMs = Date.now()): boolean {
  return hasExpiredAt(input.approvalExpiresAt, nowMs) || hasExpiredAt(input.handoffExpiresAt, nowMs);
}

export function decideApprovalUi(input: ApprovalSignals, nowMs = Date.now()): ApprovalDecision {
  const hostname = input.hostname || '';
  const expired = isApprovalSignalExpired(input, nowMs);
  const pwaInstalled = hasPwaInstallSignal(input);
  const pushStatus = input.pushDeliveryStatus || 'unknown';
  const mobilePresence = input.mobilePresence || 'unknown';
  const hasValidQr = Boolean(input.handoffAvailable && input.handoffExpiresAt && !expired);
  const showTestAccess = shouldShowTestAccess(hostname);

  if (input.checking) {
    return decision({
      state: APPROVAL_STATES.CHECKING,
      showQr: false,
      qrTitle: null,
      showFallbacks: false,
      showTestAccess,
      primaryMessage: 'Verificando celular e aplicativo.',
    });
  }

  if (expired) {
    return decision({
      state: APPROVAL_STATES.EXPIRED,
      showQr: false,
      qrTitle: null,
      showFallbacks: true,
      showTestAccess,
      primaryMessage: 'O tempo de seguranca acabou. Escolha outra forma de acesso.',
    });
  }

  if (mobilePresence === 'online') {
    return decision({
      state: APPROVAL_STATES.MOBILE_ONLINE,
      showQr: false,
      qrTitle: null,
      showFallbacks: Boolean(input.fallbacksAfterPresenceWait),
      showTestAccess,
      primaryMessage: 'Seu aplicativo esta aberto. Enviamos uma solicitacao direta.',
    });
  }

  if (!pwaInstalled && input.installQrAvailable) {
    return decision({
      state: APPROVAL_STATES.INSTALL_APP,
      showQr: true,
      qrTitle: 'Instalar aplicativo',
      showFallbacks: true,
      showTestAccess,
      primaryMessage: 'Nao encontramos o aplicativo instalado neste acesso.',
    });
  }

  if (pushStatus === 'sent') {
    return decision({
      state: APPROVAL_STATES.PUSH_SENT,
      showQr: hasValidQr,
      qrTitle: hasValidQr ? 'Autorizar por QR Code' : null,
      showFallbacks: true,
      showTestAccess,
      primaryMessage: 'Enviamos uma notificacao para o seu celular.',
    });
  }

  if (['no_token', 'token_unregistered', 'firebase_unavailable', 'failed'].includes(pushStatus)) {
    return decision({
      state: APPROVAL_STATES.PUSH_UNAVAILABLE,
      showQr: hasValidQr,
      qrTitle: hasValidQr ? 'Autorizar por QR Code' : null,
      showFallbacks: true,
      showTestAccess,
      primaryMessage: 'Nao foi possivel enviar notificacao para o celular.',
    });
  }

  if (hasValidQr) {
    return decision({
      state: APPROVAL_STATES.QR_AVAILABLE,
      showQr: true,
      qrTitle: 'Autorizar por QR Code',
      showFallbacks: true,
      showTestAccess,
      primaryMessage: 'Escaneie o QR Code com o celular autorizado.',
    });
  }

  return decision({
    state: APPROVAL_STATES.FALLBACK_ONLY,
    showQr: false,
    qrTitle: null,
    showFallbacks: true,
    showTestAccess,
    primaryMessage: 'Escolha uma forma alternativa para liberar seu acesso.',
  });
}

function decision(value: ApprovalDecision): ApprovalDecision {
  return Object.freeze(value);
}

function hasExpiredAt(expiresAt?: string, nowMs = Date.now()): boolean {
  if (!expiresAt) return false;
  const expiresMs = new Date(expiresAt).getTime();
  return Number.isFinite(expiresMs) && expiresMs <= nowMs;
}
